import { c as _c } from "react/compiler-runtime";
import { ActionIcon, ActionIconProps, Button, ButtonProps, Progress } from '@mantine/core';
import { MdClose } from 'react-icons/md';
import { ReactNode, useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { delay } from 'shared/promise';
import { pick } from 'remeda';
export const useGraceAction = <TData, TError, TVariables>({
  action,
  gracePeriod = 3000
}: {
  action: () => Promise<TData>;
  gracePeriod?: number;
}) => {
  const [progress, setProgress] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const startTime = Date.now();
      while (true) {
        if (controller.signal.aborted) break;
        await delay(100);
        const newProgress = Math.min((Date.now() - startTime) / gracePeriod * 100, 100);
        setProgress(newProgress);
        if (newProgress >= 100) break;
      }
      if (controller.signal.aborted) return;
      abortControllerRef.current = null;
      return await action();
    },
    onSettled: () => {
      setProgress(0);
      abortControllerRef.current = null;
    }
  });

  // Cleanup on unmount - just clear the reference, don't abort
  useEffect(() => {
    return () => {
      abortControllerRef.current = null;
    };
  }, []);
  return {
    onClick: () => mutation.mutate(undefined),
    inGracePeriod: abortControllerRef.current !== null,
    progress,
    inGraceCancel: () => {
      if (!abortControllerRef.current) return;
      abortControllerRef.current.abort();
    },
    ...pick(mutation, ['isPending', 'error', 'isError', 'isSuccess'])
  };
};
type UseGraceAction = ReturnType<typeof useGraceAction>;
type GraceProps = UseGraceAction & {
  cancelLabel?: ReactNode;
  children: React.ReactNode;
};
export const GraceAction = t0 => {
  const $ = _c(8);
  let cancelLabel;
  let children;
  let p;
  if ($[0] !== t0) {
    ({
      children,
      cancelLabel,
      ...p
    } = t0);
    $[0] = t0;
    $[1] = cancelLabel;
    $[2] = children;
    $[3] = p;
  } else {
    cancelLabel = $[1];
    children = $[2];
    p = $[3];
  }
  let t1;
  if ($[4] !== cancelLabel || $[5] !== children || $[6] !== p) {
    t1 = p.inGracePeriod ? <Button onClick={p.inGraceCancel} disabled={p.isPending} variant="light" color="red" size="sm" rightSection={<MdClose size={16} />}><div className="flex flex-col gap-1 items-center"><div className="text-sm">{cancelLabel ?? "Cancel"}</div><Progress value={p.progress} color="red" size="xs" className="w-full" /></div></Button> : <ActionIcon {...p}>{children}</ActionIcon>;
    $[4] = cancelLabel;
    $[5] = children;
    $[6] = p;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
export const GraceButton = t0 => {
  const $ = _c(18);
  let cancelLabel;
  let children;
  let p;
  if ($[0] !== t0) {
    ({
      children,
      cancelLabel,
      ...p
    } = t0);
    $[0] = t0;
    $[1] = cancelLabel;
    $[2] = children;
    $[3] = p;
  } else {
    cancelLabel = $[1];
    children = $[2];
    p = $[3];
  }
  const t1 = p.inGracePeriod ? p.inGraceCancel : p.onClick;
  const t2 = p.inGracePeriod ? p.isPending : p.disabled;
  let t3;
  if ($[4] !== p.inGracePeriod || $[5] !== p.rightSection) {
    t3 = p.inGracePeriod ? <MdClose size={16} /> : p.rightSection;
    $[4] = p.inGracePeriod;
    $[5] = p.rightSection;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== cancelLabel || $[8] !== children || $[9] !== p.inGracePeriod || $[10] !== p.progress) {
    t4 = p.inGracePeriod ? <div className="flex flex-col gap-1 items-center"><div className="text-sm">{cancelLabel ?? "Cancel"}</div><Progress value={p.progress} color="red" size="xs" className="w-full" /></div> : children;
    $[7] = cancelLabel;
    $[8] = children;
    $[9] = p.inGracePeriod;
    $[10] = p.progress;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== p || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = <Button {...p} onClick={t1} disabled={t2} rightSection={t3}>{t4}</Button>;
    $[12] = p;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};